import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarDays, Clock, User, Mail, Phone, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["10:00", "10:30", "11:00", "11:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"];

function nextBusinessDates(count = 7) {
  const out = [];
  const d = new Date();
  while (out.length < count) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0) { // exclude Sundays
      out.push(new Date(d));
    }
  }
  return out;
}

const TOPICS = [
  "Mutual Fund / SIP Planning",
  "Goal-Based Investing",
  "Retirement Planning",
  "Children's Education Planning",
  "Tax Saving (ELSS)",
  "HNI Portfolio Services",
  "Corporate Treasury",
  "Portfolio Review",
];

export default function BookMeetingModal({ open, onOpenChange }) {
  const dates = nextBusinessDates(7);
  const [step, setStep] = useState(0); // 0 select, 1 details, 2 success
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [topic, setTopic] = useState(TOPICS[0]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(0); setDate(null); setTime(null);
      setForm({ name: "", email: "", phone: "", notes: "" });
    }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/meeting`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        date: date.toISOString().slice(0, 10),
        time,
        topic,
        notes: form.notes,
      });
      setStep(2);
      toast.success("Meeting requested — we'll confirm shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not book. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="book-meeting-modal"
        className="bg-[#0c0c0c] border-white/10 text-white sm:max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <CalendarDays size={20} className="text-[#D4AF37]" /> Book a Consultation
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            30-minute introductory discussion with the Axomind team.
          </DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-6 mt-2">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-3">Select date</div>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="meeting-dates">
                {dates.map((d, i) => {
                  const isSel = date && d.toDateString() === date.toDateString();
                  return (
                    <button
                      key={i}
                      onClick={() => setDate(d)}
                      data-testid={`meeting-date-${i}`}
                      className={`p-2 rounded-xl border text-center transition ${
                        isSel
                          ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                          : "bg-black/30 border-white/10 text-gray-300 hover:border-[#D4AF37]/50"
                      }`}
                    >
                      <div className="text-[10px] uppercase">
                        {d.toLocaleDateString("en-IN", { weekday: "short" })}
                      </div>
                      <div className="font-display font-bold text-lg leading-none mt-1">{d.getDate()}</div>
                      <div className="text-[10px] mt-0.5">
                        {d.toLocaleDateString("en-IN", { month: "short" })}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-3">Select time (IST)</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="meeting-times">
                {SLOTS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTime(t)}
                    data-testid={`meeting-time-${t}`}
                    className={`px-3 py-2 rounded-xl border text-sm transition ${
                      time === t
                        ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                        : "bg-black/30 border-white/10 text-gray-300 hover:border-[#D4AF37]/50"
                    }`}
                  >
                    <Clock size={12} className="inline -mt-0.5 mr-1" />
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setStep(1)}
              disabled={!date || !time}
              data-testid="meeting-continue"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-50 hover:bg-[#FFD700] transition"
            >
              Continue
            </button>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="meeting-form">
            <div className="glass rounded-xl p-3 text-sm text-gray-300 flex items-center gap-3">
              <CalendarDays size={16} className="text-[#D4AF37]" />
              {date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} at {time} IST
              <button
                type="button"
                onClick={() => setStep(0)}
                className="ml-auto text-xs text-[#D4AF37] hover:underline"
              >
                Change
              </button>
            </div>

            <FieldRow icon={<User size={14} />}>
              <input
                required minLength={2}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="meeting-name"
                className="flex-1 bg-transparent outline-none text-white"
              />
            </FieldRow>
            <FieldRow icon={<Mail size={14} />}>
              <input
                required type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="meeting-email"
                className="flex-1 bg-transparent outline-none text-white"
              />
            </FieldRow>
            <FieldRow icon={<Phone size={14} />}>
              <input
                required
                placeholder="Phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="meeting-phone"
                className="flex-1 bg-transparent outline-none text-white"
              />
            </FieldRow>

            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-gray-400">Topic</label>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                data-testid="meeting-topic"
                className="mt-2 w-full bg-black/50 border border-white/10 rounded-xl px-3 py-3 text-white focus:border-[#D4AF37]/60 focus:outline-none"
              >
                {TOPICS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>

            <textarea
              rows={3}
              placeholder="Anything specific you'd like us to prepare? (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="meeting-notes"
              className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:border-[#D4AF37]/60 focus:outline-none resize-none"
            />

            <button
              type="submit"
              disabled={submitting}
              data-testid="meeting-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-black font-semibold disabled:opacity-60 hover:bg-[#FFD700] transition"
            >
              {submitting ? "Booking..." : "Confirm Booking"}
            </button>
          </form>
        )}

        {step === 2 && (
          <div className="py-8 text-center" data-testid="meeting-success">
            <CheckCircle2 size={48} className="text-[#D4AF37] mx-auto" />
            <h3 className="font-display text-2xl text-white mt-4">Meeting Requested</h3>
            <p className="text-gray-400 mt-2 max-w-sm mx-auto">
              We've received your booking for{" "}
              <span className="text-white">
                {date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
              </span>{" "}
              at <span className="text-white">{time} IST</span>. Our team will email a calendar invite within 1 business hour.
            </p>
            <button
              onClick={close}
              data-testid="meeting-close"
              className="mt-6 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-semibold hover:bg-[#FFD700] transition"
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ icon, children }) {
  return (
    <div className="flex items-center gap-3 bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus-within:border-[#D4AF37]/60 transition">
      <span className="text-gray-500">{icon}</span>
      {children}
    </div>
  );
}
